'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'

import { recordPathChange } from '@/utils/errorLog'

/**
 * Tracks SPA route changes for the Meta Pixel and Google Analytics.
 *
 * Both tags are initialized once in the root layout (app/layout.tsx), which
 * fires the initial PageView/page_view. This hook must NOT re-init them (a
 * second `fbq('init')` triggers a "Duplicate Pixel ID" warning) — it only
 * reports the view on subsequent client-side navigations, skipping the first
 * render so the initial load isn't double-counted. Pixel and GA are checked
 * independently because, loaded via `lazyOnload`, one may be ready before the
 * other.
 */
const usePageTracking = () => {
  const pathname = usePathname()
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (typeof window === 'undefined') return

    // Recorded on EVERY navigation, including the first — the error boundary
    // needs the route the user came from, and unlike the analytics calls below
    // there is nothing to double-count.
    recordPathChange(pathname)

    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    if (typeof window.fbq === 'function') window.fbq('track', 'PageView')
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', { page_path: pathname })
    }
  }, [pathname])
}

export default usePageTracking
